"use client";

import Image from "next/image";
import { Tabs } from "@/components/ui/tabs";

type TabItem = { title: string; description: string; image: string };

// Static data lives outside the component so it is created once, not on every render.
const TAB_DATA: TabItem[] = [
  {
    title: "Fintech",
    description:
      "We’re your go-to for financial innovation, banking, lending, trading, and accounting software development, e-wallets, and blockchain solutions.",
    image: "/tab/fintech.jpeg",
  },
  {
    title: "Edtech",
    description:
      "Manage schools and corporate learning with ease. We provide software development for LMS, e-learning, mobile learning apps, educational platforms, and virtual classrooms.",
    image: "/tab/edtech.jpeg",
  },
  {
    title: "Retail",
    description:
      "Revolutionize your retail operations with us. From e-commerce and POS software to omnichannel solutions and online ordering, we’ve got you covered.",
    image: "/tab/retail.jpeg",
  },
  {
    title: "Real estate",
    description:
      "Navigate real estate effortlessly. We deliver location-based services, real estate applications, ERP and process automation, property management, and digital twin solutions.",
    image: "/tab/real-estate.jpeg",
  },
];

// Function declaration (hoisted) so TABS below can use it at module load.
function TabContent({ title, description, image }: TabItem) {
  return (
    <div className="relative mx-2 h-[360px] rounded-[28px] bg-primary text-white shadow-[0_20px_40px_-12px_rgba(0,0,0,0.3)] md:h-[500px] lg:h-[560px]">
      <div className="relative z-10 flex h-full flex-col justify-start gap-8 p-6 md:p-8 lg:p-10">
        <p className="max-w-xl text-2xl font-bold md:text-3xl lg:text-5xl">{title}</p>
        <p className="max-w-xl text-sm leading-relaxed md:text-base lg:max-w-4xl lg:text-xl">
          {description}
        </p>
      </div>

      <Image
        src={image}
        alt={`${title} illustration`}
        width={1200}
        height={900}
        quality={70}
        // Mirrors the CSS widths below so the browser downloads the smallest file that fits
        sizes="(min-width: 1024px) 1200px, (min-width: 768px) 68vw, 90vw"
        className="absolute bottom-[-35%] left-1/2 z-0 h-[72%] w-[90%] -translate-x-1/2 rounded-[30px] object-cover object-center md:h-[78%] md:w-[68%] lg:h-[95%] lg:w-[95%]"
      />
    </div>
  );
}

// Elements are created once; React skips re-rendering them when the parent updates.
const TABS = TAB_DATA.map((item) => ({
  title: item.title,
  value: item.title,
  content: <TabContent {...item} />,
}));

export default function TabsDemo() {
  return (
    <div className="relative mx-auto my-12 flex h-[20rem] w-full max-w-7xl flex-col items-start justify-start [perspective:1000px] md:h-[52rem]">
      <Tabs tabs={TABS} />
    </div>
  );
}